import {
  Avatar, Grid, Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import moment from 'moment';
import { History } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { MatchStatusEnum, VIEW_MODE } from '../../../consts/enums';
import { DATE_FORMAT, MATCH_STATUS_MAP } from '../../../consts';
import { CheckCircleSolid } from '../../../icons/check-circle-solid';
import { ClockOutlined } from '../../../icons/clock-outlined';
import { PaperAirplane } from '../../../icons/paper-airplane';
import { Eye } from '../../../icons/eye';
import STRING_KEYS from '../../../language/keys';
import { PencilAlt } from '../../../icons/pencil-alt';
import PerfectButton from '../../../components/common/buttons/PerfectButton';
import Status from '../../Status';
import { MatchActionMenu } from '../MatchActionMenu';
import CopyableLabel from '../../CopyableLabel';
import useNavigate from '../../../hooks/useNavigate';
import { useAuth } from '../../../hooks/useAuth';
import { PERMISSIONS } from '../../../consts/permissions';
import { style } from './style';

type MatchHeaderProps = {
  matchId: string,
  positionId: string,
  accountId: string,
  updatedBy: string,
  createdBy: string,
  firstName: string,
  lastName: string,
  updatedAt: string,
  createdAt: string,
  imageUrl: string,
  status: string,
  onViewHistoryClick: (bool: boolean) => void,
  onChangeToDraftClick: () => void,
  onSaveDraftClick: () => void,
  onPublishClick: () => void,
  onPreviewMatchClick: () => void,
  isLoadingPreview: boolean,
  viewMode: ViewMode,
  saveDraftEnabled: boolean,
  isLocked: boolean,
}

const MatchHeader = ({
  matchId,
  positionId,
  accountId,
  updatedAt,
  updatedBy,
  createdAt,
  createdBy,
  imageUrl,
  firstName,
  lastName,
  status,
  onViewHistoryClick,
  onChangeToDraftClick,
  onSaveDraftClick: onSaveClick,
  onPublishClick,
  onPreviewMatchClick,
  isLoadingPreview,
  viewMode,
  saveDraftEnabled,
  isLocked,
}: MatchHeaderProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const { goToMatchEdit } = useNavigate();
  const { hasPermissions } = useAuth();

  const getTalentName = () => {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } if (firstName) {
      return firstName;
    } if (lastName) {
      return lastName;
    }
    
    return 'NO_AVAILABLE_NAME';
  };

  const isNewMatch = !matchId;

  const dateCreated = createdAt && moment(new Date(createdAt)).format(DATE_FORMAT.TABLE_LIST);
  const dateUpdated = updatedAt && moment(new Date(updatedAt)).format(DATE_FORMAT.TABLE_LIST);

  const unknownString = translate(STRING_KEYS.UNKNOWN);

  const publishButtonText = status === MatchStatusEnum.published ?
    translate(STRING_KEYS.REPUBLISH) :
    translate(STRING_KEYS.PUBLISH);

  const draftButtonText = (!isNewMatch && !saveDraftEnabled) ?
    translate(STRING_KEYS.POSITIONS_CREATE_NEW.BTN_DRAFT_SAVED) :
    translate(STRING_KEYS.POSITIONS_CREATE_NEW.BTN_DRAFT);

  const previewButtonText = isLoadingPreview ?
    translate(STRING_KEYS.LOADING) :
    translate(STRING_KEYS.PREVIEW);

  const renderStatus = () => {
    return  (
      <Status
        label={ translate(STRING_KEYS.MATCH_STATUSES[status]) }
        type={ MATCH_STATUS_MAP[status] }
        isLockedIcon={ isLocked }
      />
    );
  };

  const renderHistoryButton = () => {
    if (!hasPermissions([PERMISSIONS.MATCH_VIEW, PERMISSIONS.MATCH_HISTORY_LOG_VIEW])) {
      return null;
    }

    return (
      <PerfectButton
        type="icon"
        onClick={ () => onViewHistoryClick(true) }
        icon={ <History />  }
      />
    );
  };

  const renderActionMenu = () => {
    return (
      <MatchActionMenu
        id={ matchId }
        onChangeStatus={ onChangeToDraftClick }
        status={ status }
      />
    );
  };

  const renderPreviewButton = () => {
    if (!hasPermissions([PERMISSIONS.MATCH_VIEW])) {
      return null;
    }

    return (
      <PerfectButton
        title={ previewButtonText }
        onClick={ onPreviewMatchClick }
        disabled={ isLoadingPreview }
        variant="outlined"
        icon={ <Eye viewBox="0 0 20 20" />  }
      />
    );
  };

  const renderPublishAndDraftButtons = () => {
    if (
      (isNewMatch && !hasPermissions([PERMISSIONS.MATCH_CREATE])) ||
      (!isNewMatch && !hasPermissions([PERMISSIONS.MATCH_EDIT]))
    ) {
      return null;
    }

    return (
      <>
        { status ===  MatchStatusEnum.draft && (
          <PerfectButton
            disabled={ !saveDraftEnabled }
            title={ draftButtonText }
            onClick={ onSaveClick }
            bgColor="primary.main"
            icon={ <CheckCircleSolid viewBox="-1 -1 18 18" /> }
          />
        ) }
        <PerfectButton
          type="submit"
          title={ publishButtonText }
          onClick={ onPublishClick }
          icon={ <PaperAirplane viewBox="0 0 20 20" /> }
        />
      </>
    );
  };

  const renderActionButtons = () => {
    return (
      <Grid item sx={ style.btns.container }>
        {renderPublishAndDraftButtons()}

        {viewMode === VIEW_MODE.READ_ONLY && hasPermissions([PERMISSIONS.MATCH_EDIT]) && (
          <PerfectButton
            type="submit"
            bgColor="primary.main"
            title="EDIT"
            onClick={ () => {
              goToMatchEdit(positionId, accountId, matchId);
            } }
            icon={ <PencilAlt /> }
          />
        )}
      </Grid>
    );
  };

  return (
    <Grid
      container
      columns={ style.mainContainer.columns }
      mt={ style.mainContainer.mt }
      style={ style.header.style }
    >
      <Grid item sx={ style.header.sx }>
        <Grid item xs={ 1 }>
          <Avatar
            style={ style.avatar }
            alt={ getTalentName() }
            src={ imageUrl }
          >
            {firstName?.charAt(0) + lastName?.charAt(0)}
          </Avatar>
        </Grid>
        <Grid item sx={ style.info }>
          <Grid sx={ { display: 'flex', justifyContent: 'flex-end', width: '100%' } }>
            <Grid item sx={ { flex: 1 } }>
              <Typography variant={ style.typography.variant } mb={ style.typography.marginBottom }>
                {getTalentName()}
              </Typography>
            </Grid>
            <Grid item sx={ style.btns.container }>
              {renderHistoryButton()}
              {renderActionMenu()}
              {renderPreviewButton()}
              {renderActionButtons()}
            </Grid>
          </Grid>
          <Grid style={ { display: 'flex', width: '100%' } }>
            <Box sx={ style.talentInfo.blockInfo }>
              {renderStatus()}
              {matchId && (
                <CopyableLabel labelText={ translate(STRING_KEYS.ID, { id: matchId }) }
                  dataToCopy={ matchId }
                  variant="caption"
                  maxWidth="18rem"
                  textColor="text.secondary"
                />
              )}
              { dateCreated && (
                <Box sx={ style.creationStamp }>
                  <ClockOutlined style={ {
                    width: 20,
                    height: 20,
                    marginRight: 3,
                    marginTop: -3,
                  } }
                  />
                  <Typography
                    variant="caption"
                    color={ style.talentInfo.color }
                    style={ style.talentInfo.createInfo }
                  >
                    {translate(STRING_KEYS.CREATED_BY_AT,
                      {
                        date: dateCreated,
                        username: createdBy || unknownString,
                      },
                    )}
                  </Typography>

                  { dateUpdated && (
                    <Typography
                      variant="caption"
                      color={ style.talentInfo.color }
                      style={ style.talentInfo.updateInfo }
                    >
                      {translate(STRING_KEYS.UPDATED_BY_AT,
                        {
                          date: dateUpdated,
                          username: updatedBy || unknownString,
                        })}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default React.memo(MatchHeader);
